/* The popup surface every Base UI menu in this app wears — the set-type picker on a set badge, the
 * exercise ⋯, and the session card's ⋯ on the week canvas. One stylesheet rather than three
 * near-identical blocks: the same eleven declarations in three files is the near-duplicate failure
 * `CLAUDE.md` names, and all three menus are the same object with different items in it.
 *
 * Promoted out of `features/plan-day-programming/ui/Menu.module.css` when the week canvas became its
 * third consumer (PR-E) — the second-consumer rule, applied one consumer late because the first two
 * were in one slice.
 *
 * Base UI supplies the behaviour — focus, dismissal, positioning, roving highlight, `role="menu"` —
 * and every visual byte is ours. It ships no CSS at all here, deliberately: `main.tsx` mounts
 * `<CSPProvider disableStyleElements>` because the production policy's `style-src-elem 'self'`
 * refuses an injected inline `\3c style>`, and the failure is invisible in development. */

/* Above the dock, which is `z-index: 20`. A menu portalled into `<body>` with no z-index of its own
 * paints BELOW a fixed ancestor that has one — the popup is then visible, hoverable and completely
 * unclickable, because the dock above it keeps the pointer.
 *
 * It has to sit on the POSITIONER and not on the popup: `z-index` applies to positioned elements
 * only, Base UI positions through inline styles on the positioner, and the popup inside it is
 * `position: static`. Declaring it on the popup does nothing at all — which is exactly what it did,
 * and only driving the menu showed it. */
.positioner {
  z-index: 40;
}

/* ── The height bound, and why every menu in the app needs one ───────────────────────────────
 * Base UI positions and flips a menu, but it does not size it: with no `max-height` the popup is
 * as tall as its items and simply hangs off the bottom edge once the trigger sits low enough or
 * the window is short enough. Measured, not reasoned about — the session card's day-move menu is
 * 330px tall (seven weekdays plus two acts), and at a 460px viewport its last three entries were
 * below the fold with `max-height: none`, `overflow: visible` and nothing on the page able to
 * scroll to them. A portalled `position: fixed` popup is not in the document's scroll flow, so
 * "the page scrolls" is not a way out: that is a clip with no scroll path, which is the one
 * outcome `CLAUDE.md` rules out outright.
 *
 * `--available-height` is Base UI's own measurement — the room between the trigger and the
 * viewport edge, already accounting for the side the menu flipped to — and it is set on the
 * POSITIONER, which this popup is the child of, so it inherits. The fallback is not decoration: an
 * undefined custom property voids the WHOLE declaration rather than falling back to the initial
 * value, so a popup ever rendered outside a positioner would silently lose its bound and be
 * exactly as broken as before. `--overlay-max-block` is that floor.
 *
 * `overflow-y: auto` is what turns the bound from a clip into a scroll. It costs nothing at the
 * heights where every item already fits, because the popup is only as tall as its content. */
.popup {
  display: flex;
  flex-direction: column;
  gap: var(--space-1px);
  min-width: var(--menu-min-width, 208px);

  /* A caller may resize this surface — and only through these four properties, never by redeclaring
   * `max-width`, `padding` or `border-radius` from its own module: two modules writing one
   * declaration is settled by chunk order, which differs between the dev server and the build, and
   * this repo has already paid for that once. A custom property set on the same element always
   * wins, whichever file lands first.
   *
   * `--menu-pad` and `--menu-radius` were added on 8 September 2026, when the programator stated its
   * own numbers for the day panel's two popups (7px at radius 13, and 8px at radius 12) against this
   * surface's 4px at radius 10. The default is unchanged, so the other three menus in the app move
   * by nothing. */
  max-width: var(--menu-max-width, 320px);
  max-height: var(--available-height, var(--overlay-max-block));
  padding: var(--menu-pad, var(--space-1));
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--menu-radius, var(--radius-sm));
  box-shadow: var(--shadow-pop);
}

.item {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-6px) var(--space-2);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text);
  cursor: default;
  border-radius: var(--radius-xs);
}

/* A menu item is not a tab stop, so the roving highlight is the ONLY focus signal a keyboard user
 * gets — and the chip fill alone measures about 1.1:1 against the popup. `--ring-menu` is the
 * repo's answer: an inset `--text` ring, because the accent on `--bg-chip` measures 2.78:1 in the
 * light theme and fails 1.4.11. */
.item[data-highlighted] {
  background: var(--bg-chip);
  box-shadow: var(--ring-menu);
}

/* Never `opacity`: dimming an item multiplies its ink's contrast down and takes the focus ring with
 * it, so a disabled entry becomes unreadable exactly when it is explaining itself. `--text-2` is
 * the repo's readable secondary and the dashed rule beside it is the second channel. */
.item[data-disabled] {
  color: var(--text-2);
}

.itemLabel {
  flex: 1;
  min-width: 0;
}

/* The reason a disabled item cannot act, printed under it rather than only on `title`: a `title`
 * reaches neither a touch user nor a keyboard user who never hovers. */
.reason {
  display: block;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.current {
  flex: none;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

/* An item whose act cannot be taken back. One rule, in the one place a menu's parts are declared.
 *
 * It existed as a private `.destructive` in four different modules — the roster's client menu, the
 * end-coaching menu, the calendar's session card menu and the plan menu that borrowed the card's —
 * all four spelling the identical single declaration. They were collapsed into this on 17 September
 * 2026 when the builder's plan menu would have been the fifth.
 *
 * `--danger-text-strong` rather than `--danger`: the marker colour is for a surface, and this is a
 * word on the menu's own ground. The mark is never the only channel — the label of every item that
 * carries it ends in an ellipsis and raises a confirm. */
.itemDestructive {
  color: var(--danger-text-strong);
}

.separator {
  height: var(--border-hairline);
  margin: var(--space-1) var(--space-2);
  background: var(--border);
}

.groupLabel {
  padding: var(--space-1) var(--space-2);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-load);
  text-transform: uppercase;
  color: var(--text-2);
}

/* ── A checkbox list, as the 02.08 gallery draws its "Parametry serii" panel ────────────────
 * A ticked box, then the parameter, then the shape it takes — "kg lub % 1RM", "3-1-1". The box is a
 * SECOND channel beside `aria-checked` rather than the only one, which is the same rule the library
 * rail's facet menu already follows. */

.mark {
  display: grid;
  flex: none;
  place-items: center;
  width: 15px;
  height: 15px;
  font-size: var(--fs-tag);
  font-weight: var(--fw-extrabold);
  color: var(--bg-card);
  background: var(--text);
  border: var(--border-hairline) solid var(--text);
  border-radius: var(--radius-hair);
}

/* Unticked: an empty outline. `:empty` rather than a modifier class, because the mark's own text is
 * exactly what says which state it is in. */
.mark:empty {
  background: transparent;
  border-color: var(--border-2);
}

.itemHint {
  flex: none;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

/* The sentence under a panel — what a removal keeps. Separated by a rule, like the gallery's. */
.footnote {
  padding: var(--space-2) var(--space-2) var(--space-1);
  margin-top: var(--space-3px);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  border-top: var(--border-hairline) solid var(--border);
}
